import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, Edit2, Check, X, Download, BookOpen, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Input } from './@/components/ui/input';
// Card/CardContent replaced with plain divs for consistent collapsible styling
import { Button } from './@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './@/components/ui/select';
import './ConfigPanel.css';

const CATEGORIES = ['hours', 'availability', 'shift', 'role', 'general'];
const SOURCES = ['gm_manual', 'system', 'compliance'];

const CustomRulesPanel = ({ customRules, setCustomRules, onDownload, ruleResults }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newRule, setNewRule] = useState({ rule_description: '', category: 'hours', source: 'gm_manual' });
  const [expandedLogs, setExpandedLogs] = useState({});

  // Build a lookup from ruleId -> result
  const resultsByRuleId = {};
  if (ruleResults && ruleResults.ruleResults) {
    ruleResults.ruleResults.forEach(r => {
      resultsByRuleId[r.ruleId] = r;
    });
  }

  const hasResults = ruleResults && ruleResults.ruleResults && ruleResults.ruleResults.length > 0;

  const toggleLogs = (ruleId) => {
    setExpandedLogs(prev => ({ ...prev, [ruleId]: !prev[ruleId] }));
  };

  const handleAdd = () => {
    if (!newRule.rule_description.trim()) return;
    const id = `rule-${Date.now()}`;
    setCustomRules([...customRules, { id, ...newRule }]);
    setNewRule({ rule_description: '', category: 'hours', source: 'gm_manual' });
    setIsAdding(false);
  };

  const handleEdit = (rule) => {
    setEditingId(rule.id);
    setEditForm({ ...rule });
  };

  const handleSaveEdit = () => {
    if (!editForm.rule_description.trim()) return;
    setCustomRules(customRules.map(r => r.id === editingId ? { ...editForm } : r));
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleDelete = (id) => {
    setCustomRules(customRules.filter(r => r.id !== id));
  };

  const renderBadge = (text, color) => (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: color,
      color: '#fff',
      marginRight: '4px',
    }}>
      {text}
    </span>
  );

  const categoryColor = (cat) => {
    const colors = { hours: '#6366f1', availability: '#0891b2', shift: '#d97706', role: '#059669', general: '#6b7280' };
    return colors[cat] || '#6b7280';
  };

  const sourceColor = (src) => {
    const colors = { gm_manual: '#8b5cf6', system: '#3b82f6', compliance: '#ef4444' };
    return colors[src] || '#6b7280';
  };

  const renderStatusIndicator = (result) => {
    if (!result) return null;

    if (result.met && result.partiallyMet) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <AlertTriangle size={16} color="#d97706" />
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#d97706' }}>Partially Met</span>
        </div>
      );
    }
    if (result.met) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <CheckCircle size={16} color="#059669" />
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>Met</span>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <XCircle size={16} color="#dc2626" />
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Not Met</span>
      </div>
    );
  };

  const getStatusBorderColor = (result) => {
    if (!result) return '#e9ecef';
    if (result.met && result.partiallyMet) return '#fbbf24';
    if (result.met) return '#34d399';
    return '#fca5a5';
  };

  const renderRuleForm = (rule, setRule, onSave, onCancel) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      <Input
        value={rule.rule_description}
        onChange={(e) => setRule({ ...rule, rule_description: e.target.value })}
        placeholder="Rule description..."
        style={{ width: '100%' }}
      />
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Select value={rule.category} onValueChange={(val) => setRule({ ...rule, category: val })}>
          <SelectTrigger style={{ width: '140px' }}>
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={rule.source} onValueChange={(val) => setRule({ ...rule, source: val })}>
          <SelectTrigger style={{ width: '140px' }}>
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            {SOURCES.map(src => (
              <SelectItem key={src} value={src}>{src}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={onSave} style={{ backgroundColor: '#059669', color: '#fff' }}>
          <Check size={14} />
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X size={14} />
        </Button>
      </div>
    </div>
  );

  // Summary counts
  const metCount = hasResults ? ruleResults.ruleResults.filter(r => r.met && !r.partiallyMet).length : 0;
  const partialCount = hasResults ? ruleResults.ruleResults.filter(r => r.met && r.partiallyMet).length : 0;
  const notMetCount = hasResults ? ruleResults.ruleResults.filter(r => !r.met).length : 0;

  return (
    <div className="collapsible-section">
      <div
        className="collapsible-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h2 className="collapsible-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <BookOpen style={{ marginRight: '0.25rem' }} />
          Custom Rules ({customRules.length})
          {hasResults && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px', fontSize: '0.8rem', fontWeight: 500 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#059669' }}>
                <CheckCircle size={14} /> {metCount}
              </span>
              {partialCount > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#d97706' }}>
                  <AlertTriangle size={14} /> {partialCount}
                </span>
              )}
              {notMetCount > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#dc2626' }}>
                  <XCircle size={14} /> {notMetCount}
                </span>
              )}
            </span>
          )}
        </h2>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      {isOpen && (
        <div className="config-panel-content">
          {hasResults && (
            <div style={{
              padding: '10px 14px',
              backgroundColor: '#f8fafc',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              marginBottom: '8px',
              fontSize: '0.8rem',
              color: '#475569',
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
            }}>
              <span><strong>{ruleResults.rulesMetFinal ?? 0}</strong> / {ruleResults.ruleResults.length} rules met</span>
              {ruleResults.pipelinePassesUsed > 0 && (
                <span>{ruleResults.pipelinePassesUsed} pipeline pass{ruleResults.pipelinePassesUsed !== 1 ? 'es' : ''}</span>
              )}
              {ruleResults.totalModifications > 0 && (
                <span>{ruleResults.totalModifications} modification{ruleResults.totalModifications !== 1 ? 's' : ''} applied</span>
              )}
            </div>
          )}

          {customRules.length === 0 && !isAdding && (
            <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No custom rules defined.</p>
          )}

          {customRules.map(rule => {
            const result = resultsByRuleId[rule.id];

            return (
              <div key={rule.id} style={{
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                border: `1px solid ${getStatusBorderColor(result)}`,
                borderLeftWidth: result ? '4px' : '1px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                {editingId === rule.id ? (
                  renderRuleForm(
                    editForm,
                    setEditForm,
                    handleSaveEdit,
                    handleCancelEdit
                  )
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <p style={{ margin: 0, fontWeight: 500, color: '#343a40' }}>
                            {rule.rule_description}
                          </p>
                          {renderStatusIndicator(result)}
                        </div>
                        <div>
                          {renderBadge(rule.category, categoryColor(rule.category))}
                          {renderBadge(rule.source, sourceColor(rule.source))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(rule)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(rule.id)}
                          style={{ color: '#dc3545', borderColor: '#dc3545' }}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    {result && result.modificationLogs && result.modificationLogs.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <button
                          onClick={() => toggleLogs(rule.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: '#6366f1',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {expandedLogs[rule.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          {expandedLogs[rule.id] ? 'Hide' : 'Show'} {result.modificationLogs.length} modification{result.modificationLogs.length !== 1 ? 's' : ''}
                        </button>
                        {expandedLogs[rule.id] && (
                          <div style={{
                            marginTop: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '0.375rem',
                            border: '1px solid #e2e8f0',
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}>
                            {result.modificationLogs.map((log, i) => (
                              <div key={i} style={{
                                padding: '3px 0',
                                borderBottom: i < result.modificationLogs.length - 1 ? '1px solid #f1f5f9' : 'none',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                color: '#334155',
                              }}>
                                {log}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {isAdding ? (
            <div style={{
              padding: '12px',
              backgroundColor: '#f0fdf4',
              borderRadius: '0.5rem',
              border: '1px dashed #059669',
            }}>
              {renderRuleForm(
                newRule,
                setNewRule,
                handleAdd,
                () => { setIsAdding(false); setNewRule({ rule_description: '', category: 'hours', source: 'gm_manual' }); }
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="outline" onClick={() => setIsAdding(true)}>
                <Plus size={16} style={{ marginRight: '4px' }} /> Add Rule
              </Button>
              <Button variant="outline" onClick={onDownload}>
                <Download size={16} style={{ marginRight: '4px' }} /> Download Custom Rules
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomRulesPanel;
